import Formatters from '@dashhudson/dashing-ui/utils/formatters';
import format from 'date-fns/format';
import dayjs from 'dayjs';
import clamp from 'lodash/clamp';
import cloneDeep from 'lodash/cloneDeep';
import isPlainObject from 'lodash/isPlainObject';

import {
  creatorPerformanceMetricTooltips,
  creatorPostsTypeConfigs,
  creatorSource,
  IGCM_ONLY_FILTERS,
  INDEX_ONLY_FILTERS,
  instagramFilterInputLimit,
  instagramPostInsightsMetricTooltips,
  instagramStoryInsightsMetricTooltips,
  inviteStatusDetails,
  metricDetails,
  PLATFORM_PRIORITY,
  profileHeaderLocationTooltipAvailable,
  profileHeaderLocationTooltipLoading,
  profileHeaderLocationTooltipUnavailable,
  tikTokAccountMetricTooltips,
  tikTokAccountPerformanceMetricTooltips,
  tikTokCreatorMetricTooltips,
  tikTokDiscoveryAgeOptions,
  tiktokDiscoveryFilterTooltips,
  tiktokFilterInputLimit,
  tiktokPostInsightsMetricTooltips,
  youtubePerformanceMetricTooltips,
  youtubePostInsightsMetricTooltips,
} from '@/app/creators/constants';
import { visualSources } from '@/app/library/constants';
import { POPULAR_FILE_EXTENSIONS } from '@/constants';
import { env } from '@/env';
import { SENTIMENTS } from '@/models/sentiment.enum';
import { getCurrentDate } from '@/utils';
import { getCountryFlag, getCountryName } from '@/utils/countries';
import { base64ToBinaryArray } from '@/utils/gmailApi';
import { engagementRating } from '@/utils/vision';

const FORWARD_MESSAGE_HEADER = '---------- Forwarded message ---------';

export function formatBirthday({ day, month, year }) {
  if (!day && !month && !year) return '-';
  let str = '';
  if (month) {
    str += new Intl.DateTimeFormat('en-US', { month: 'long', timeZone: 'UTC' }).format(
      Date.UTC(2021, month - 1),
    );
  }
  if (day) {
    str += ` ${day}`;
    if (year) {
      return `${str}, ${year}`;
    }
  }
  if (year) {
    return `${str} ${year}`;
  }
  return str;
}

export function formatAddress({ country, region, city, mailingAddress, postalCode }) {
  if (!country && !region && !city && !mailingAddress && !postalCode) return '-';
  const values = [mailingAddress, city, region, postalCode];
  if (country) values.push(getCountryName(country));
  return values.filter((value) => value).join(', ');
}

export function checkCreatorDetailsExist(creator) {
  if (!creator) return false;

  return Object.values({
    creatorName: creator.creatorName,
    email: creator.email,
    notes: creator.notes,
    ...creator.birthday,
    ...creator.address,
    ...creator.phone,
  }).some((value) => value);
}

export function replaceEmptyStringsWithNull(obj) {
  if (typeof obj === 'object' && obj !== null) {
    Object.keys(obj).forEach((key) => {
      if (obj[key] === '') {
        obj[key] = null;
      } else if (typeof obj[key] === 'object') {
        replaceEmptyStringsWithNull(obj[key]);
      }
    });
  }
  return obj;
}

export function diffCreatorDetails(originalDetails, updatedDetails) {
  return Object.keys({ ...originalDetails, ...updatedDetails }).reduce((diff, key) => {
    const originalValue = originalDetails[key];
    const updatedValue = updatedDetails[key];
    if (!Object.is(originalValue, updatedValue)) {
      if (isPlainObject(originalValue) && isPlainObject(updatedValue)) {
        const nestedDiff = diffCreatorDetails(originalValue, updatedValue);
        if (Object.keys(nestedDiff).length) {
          diff[key] = { ...originalValue, ...nestedDiff };
        }
      } else {
        diff[key] = updatedValue;
      }
    }
    return diff;
  }, {});
}

export function findChangedKeys(originalDetails, updatedDetails, parentKey = '') {
  const nonCustomFieldKeys = [];
  const customFieldKeys = [];

  Object.keys({ ...originalDetails, ...updatedDetails }).forEach((key) => {
    const originalValue = originalDetails[key];
    const updatedValue = updatedDetails[key];
    const fullKey = parentKey ? `${parentKey}.${key}` : key;

    if (updatedValue === undefined) {
      return;
    }

    if (key === 'customFields' && Array.isArray(originalValue) && Array.isArray(updatedValue)) {
      const updatedFieldKeys = new Set();
      const originalMap = Object.fromEntries(originalValue.map((item) => [item.name, item]));
      const updatedMap = Object.fromEntries(updatedValue.map((item) => [item.name, item]));

      Object.keys({ ...originalMap, ...updatedMap }).forEach((fieldKey) => {
        const originalItem = originalMap[fieldKey];
        const updatedItem = updatedMap[fieldKey];
        if (!originalItem || !updatedItem || originalItem.value !== updatedItem.value) {
          updatedFieldKeys.add(fieldKey);
        }
      });

      if (updatedFieldKeys.size > 0) {
        nonCustomFieldKeys.push('Custom');
        customFieldKeys.push(...updatedFieldKeys);
      }
      return;
    }

    if (!Object.is(originalValue, updatedValue)) {
      if (isPlainObject(originalValue) && isPlainObject(updatedValue)) {
        const { nonCustomFieldKeys: nestedNonCustom, customFieldKeys: nestedCustom } =
          findChangedKeys(originalValue, updatedValue, fullKey);
        nonCustomFieldKeys.push(...nestedNonCustom);
        customFieldKeys.push(...nestedCustom);
      } else {
        nonCustomFieldKeys.push(fullKey);
      }
    }
  });

  return { nonCustomFieldKeys, customFieldKeys };
}

export function cleanCustomFields(creatorDetails) {
  let customFields = cloneDeep(creatorDetails.customFields);

  customFields = customFields?.filter((customField) => customField.value);

  customFields?.forEach((customField) => {
    delete customField.name;
    delete customField.showInRequestForm;
  });

  return customFields;
}

export function mapGlobalCustomFieldsToCreator(creator, globalCustomFields) {
  return globalCustomFields.value?.map((globalCustomField) => {
    const creatorCustomField = creator?.customFields?.find(
      (item) => item.id === globalCustomField.id,
    );
    return {
      ...globalCustomField,
      value: creatorCustomField?.value,
    };
  });
}

export function mapApiResponseToForm(creatorDetails, globalCustomFields) {
  if (!creatorDetails) {
    return {
      phone: {
        countryCode: '',
        nationalNumber: '',
      },
      customFields: globalCustomFields,
    };
  }

  const customFields = mapGlobalCustomFieldsToCreator(creatorDetails, globalCustomFields);

  return {
    creatorName: creatorDetails.creatorName,
    email: creatorDetails.email,
    notes: creatorDetails.notes,
    phone: {
      nationalNumber: creatorDetails.phone?.nationalNumber || '',
    },
    address: creatorDetails.address,
    birthday: creatorDetails.birthday,
    customFields,
  };
}

export function formatDate(timestamp) {
  let date;

  // If it's a string with 10 digits, assume it's a unix timestamp in seconds.
  if (typeof timestamp === 'string' && /^\d{10}$/.test(timestamp)) {
    date = new Date(parseInt(timestamp, 10) * 1000);
  } else {
    date = new Date(timestamp);
  }

  return format(date, 'MMM dd, hh:mm a');
}

export function formatTikTokVacationDate(datetime) {
  const date = new Date(datetime);

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

const handleNumberFormatting = (metricValue, baseResult) => {
  if (metricValue < 1) return { ...baseResult, formatOptions: { maximumFractionDigits: 1 } };
  if (metricValue >= 1 && metricValue < 1000000)
    return { ...baseResult, formatOptions: { maximumFractionDigits: 0 } };
  if (metricValue >= 1000000)
    return {
      ...baseResult,
      formatOptions: {
        maximumFractionDigits: 2,
        notation: 'compact',
        compactDisplay: 'short',
      },
      popover: { title: metricValue },
    };
  return null;
};

const handleCurrencyFormatting = (metricValue, baseResult) => {
  if (metricValue >= 1 && metricValue < 1000)
    return { ...baseResult, formatOptions: { maximumFractionDigits: 2 } };
  if (metricValue >= 1000) return { ...baseResult, formatOptions: { maximumFractionDigits: 0 } };
  return null;
};

export function getMetricFormatAndPopover(metricFormat, metricType, metricValue) {
  if (!metricFormat || !metricType) return null;
  if (!metricValue || metricValue === '-') return null;

  const baseResult = { formatOptions: {}, popover: {} };

  switch (metricFormat) {
    case 'number':
      return handleNumberFormatting(metricValue, baseResult);
    case 'currency':
      return handleCurrencyFormatting(metricValue, baseResult);
    case 'percent':
    case 'average':
      return { ...baseResult, formatOptions: { maximumFractionDigits: 2 } };
    default:
      return baseResult;
  }
}

export function getInstagramAccountPerformance(creator) {
  return [
    {
      title: 'Followers',
      value: creator?.rollingAccountMetrics.followers,
      tooltip: creatorPerformanceMetricTooltips.FOLLOWERS,
      format: 'number',
      type: 'Total',
    },
    {
      title: 'Avg. Eng. Rate',
      value: creator?.rollingAccountMetrics.avgEngagementRate,
      tooltip: creatorPerformanceMetricTooltips.AVG_ENGAGEMENT_RATE,
      format: 'percent',
      type: 'Average',
    },
    {
      title: 'Avg. EMV',
      value: creator?.rollingAccountMetrics.avgEmv,
      tooltip: creatorPerformanceMetricTooltips.TOTAL_EMV,
      format: 'currency',
      type: 'Total',
    },
    {
      title: 'Avg. Engagements',
      value: creator?.rollingAccountMetrics.avgEngagements,
      tooltip: creatorPerformanceMetricTooltips.AVG_ENGAGEMENTS,
      format: 'number',
      type: 'Average',
    },
    {
      title: 'Avg. Effectiveness',
      value: creator?.rollingAccountMetrics.avgEffectiveness,
      tooltip: creatorPerformanceMetricTooltips.AVG_EFFECTIVENESS,
      format: 'percent',
      type: 'Average',
    },
    {
      title: 'Avg. Weekly Posts',
      value: creator?.rollingAccountMetrics.avgWeeklyPosts,
      tooltip: creatorPerformanceMetricTooltips.AVG_WEEKLY_POSTS,
      format: 'number',
      type: 'Average',
    },
  ];
}

export function getInstagramAccountMetrics(account) {
  return [
    {
      title: 'Followers',
      value: account?.followers,
      tooltip: creatorPerformanceMetricTooltips.FOLLOWERS,
      format: 'number',
      type: 'Total',
    },
    {
      title: 'Avg. Eng. Rate',
      value: account?.instagram?.engagementRate,
      tooltip: creatorPerformanceMetricTooltips.AVG_ENGAGEMENT_RATE,
      format: 'percent',
      type: 'Average',
    },
    {
      title: 'Avg. Engagements',
      value: account?.instagram?.engagements,
      tooltip: creatorPerformanceMetricTooltips.AVG_ENGAGEMENTS,
      format: 'number',
      type: 'Average',
    },
    {
      title: 'Avg. Effectiveness',
      value: account?.avgEffectivenessRate,
      tooltip: creatorPerformanceMetricTooltips.AVG_EFFECTIVENESS,
      format: 'percent',
      type: 'Average',
    },
    {
      title: 'Avg. Estimated Reach',
      value: account?.avgEstimatedReach,
      tooltip: creatorPerformanceMetricTooltips.AVG_ESTIMATED_REACH,
      format: 'number',
      type: 'Average',
    },
    {
      title: 'Avg. Weekly Posts',
      value: account?.avgPostsPerWeek,
      tooltip: creatorPerformanceMetricTooltips.AVG_WEEKLY_POSTS,
      format: 'number',
      type: 'Average',
    },
    {
      title: 'Engaged Accounts',
      value: account?.instagram?.engagedAccounts,
      tooltip: creatorPerformanceMetricTooltips.ENGAGED_ACCOUNTS,
      format: 'number',
      type: 'Total',
      igcmMetric: true,
    },
    {
      title: 'Reach',
      value: account?.instagram?.reach,
      tooltip: creatorPerformanceMetricTooltips.REACH,
      format: 'number',
      type: 'Total',
      igcmMetric: true,
    },
    {
      title: 'Interaction Rate',
      value:
        account?.instagram?.reelsInteractionRate != null
          ? account.instagram.reelsInteractionRate / 100
          : null,
      tooltip: creatorPerformanceMetricTooltips.INTERACTION_RATE,
      format: 'percent',
      type: 'Average',
      igcmMetric: true,
    },
    {
      title: 'Hook Rate',
      value:
        account?.instagram?.reelsHookRate != null ? account.instagram.reelsHookRate / 100 : null,
      tooltip: creatorPerformanceMetricTooltips.HOOK_RATE,
      format: 'percent',
      type: 'Average',
      igcmMetric: true,
    },
  ];
}

export function getTikTokAccountMetrics(account, avgWeeklyPostsPending) {
  return [
    {
      title: 'Followers',
      value: account?.followers,
      tooltip: tikTokAccountMetricTooltips.FOLLOWERS,
      format: 'number',
      type: 'Total',
    },
    {
      title: 'Avg. Eng. Rate',
      value: account?.avgEngagementRate,
      tooltip: tikTokAccountMetricTooltips.AVG_ENGAGEMENT_RATE,
      format: 'percent',
      type: 'Average',
    },
    {
      title: 'Avg. Engagements',
      value: account?.avgEngagements,
      tooltip: tikTokAccountMetricTooltips.AVG_ENGAGEMENTS,
      format: 'number',
      type: 'Average',
    },
    {
      title: 'Avg. Video Views',
      value: account?.avgVideoViews,
      tooltip: tikTokAccountMetricTooltips.AVG_VIDEO_VIEWS,
      format: 'number',
      type: 'Average',
    },
    {
      title: 'Avg. Comments',
      value: account?.avgComments,
      tooltip: tikTokAccountMetricTooltips.AVG_COMMENTS,
      format: 'number',
      type: 'Average',
    },
    {
      title: 'Avg. Weekly Posts',
      value: account?.avgWeeklyPosts,
      tooltip: tikTokAccountMetricTooltips.AVG_WEEKLY_POSTS,
      format: 'number',
      type: 'Average',
      loading: avgWeeklyPostsPending,
    },
  ];
}

export const getTikTokDiscoveryProfileMetrics = (account, avgWeeklyPostsPending) => [
  {
    title: 'Followers',
    value: account?.followers,
    tooltip: tikTokAccountMetricTooltips.FOLLOWERS,
    format: 'number',
    type: 'Total',
    channel: creatorSource.TIKTOK,
  },
  {
    title: 'Avg. Eng. Rate',
    value: account?.avgEngagementRate,
    tooltip: tikTokAccountPerformanceMetricTooltips.AVG_ENGAGEMENT_RATE,
    format: 'percent',
    type: 'Average',
    channel: creatorSource.TIKTOK,
  },
  {
    title: 'Avg. Engagements',
    value: account?.avgEngagements,
    tooltip: tikTokAccountPerformanceMetricTooltips.AVG_ENGAGEMENTS,
    format: 'number',
    type: 'Average',
    channel: creatorSource.TIKTOK,
  },
  {
    title: 'Avg. Video Views',
    value: account?.avgVideoViews,
    tooltip: tikTokAccountPerformanceMetricTooltips.AVG_VIDEO_VIEWS,
    format: 'number',
    type: 'Average',
    channel: creatorSource.TIKTOK,
  },
  {
    title: 'Avg. Comments',
    value: account?.avgComments,
    tooltip: tikTokAccountPerformanceMetricTooltips.AVG_COMMENTS,
    format: 'number',
    type: 'Average',
    channel: creatorSource.TIKTOK,
  },
  {
    title: 'Avg. Weekly Posts',
    value: account?.avgWeeklyPosts,
    tooltip: tikTokAccountPerformanceMetricTooltips.AVG_WEEKLY_POSTS,
    format: 'number',
    type: 'Average',
    channel: creatorSource.TIKTOK,
    loading: avgWeeklyPostsPending,
  },
  {
    title: 'Median Views',
    value: account?.medianViews,
    tooltip: tikTokAccountPerformanceMetricTooltips.MEDIAN_VIEWS,
    format: 'number',
    type: 'Total',
    channel: creatorSource.TIKTOK,
  },
];

export function getTikTokAccountPerformanceMetrics(
  account,
  hasMultiChannels,
  avgWeeklyPostsPending,
) {
  const isConnected = account?.invitationStatus === inviteStatusDetails.ACCEPTED.value;
  const isInTTCM = account?.ttcmStatus === 'IN';

  const notConnectedInTTCMMetrics = [
    'Avg. Engagements',
    'Avg. Video Views',
    'Avg. Weekly Posts',
    'EMV',
    'Likes',
    'Video Count',
  ];
  const connectedNotInTTCMMetrics = [
    'Avg. Engagements',
    'Avg. Video Views',
    'Avg. Weekly Posts',
    'EMV',
  ];

  const metrics = [
    {
      title: 'Avg. Eng. Rate',
      value: account?.engagementRate,
      tooltip: tikTokAccountPerformanceMetricTooltips.AVG_ENGAGEMENT_RATE,
      format: 'percent',
      type: 'Average',
    },
    {
      title: 'EMV',
      value: account?.totalEmv,
      tooltip: tikTokAccountPerformanceMetricTooltips.TOTAL_EMV,
      format: 'currency',
      type: 'Total',
    },
    {
      title: 'Likes',
      value: account?.totalLikes,
      tooltip: tikTokAccountPerformanceMetricTooltips.TOTAL_LIKES,
      format: 'number',
      type: 'Total',
    },
    {
      title: 'Video Count',
      value: account?.totalPostCount,
      tooltip: tikTokAccountPerformanceMetricTooltips.VIDEO_COUNT,
      format: 'number',
      type: 'Total',
    },
  ];

  const multiChannelMetrics = [
    {
      title: 'Avg. Engagements',
      value: account?.avgEngagements,
      tooltip: tikTokAccountPerformanceMetricTooltips.AVG_ENGAGEMENTS,
      format: 'number',
      type: 'Average',
    },
    {
      title: 'Avg. Video Views',
      value: account?.avgVideoViews,
      tooltip: tikTokAccountPerformanceMetricTooltips.AVG_VIDEO_VIEWS,
      format: 'number',
      type: 'Average',
    },
    {
      title: 'Avg. Weekly Posts',
      value: account?.avgWeeklyPosts,
      tooltip: tikTokAccountPerformanceMetricTooltips.AVG_WEEKLY_POSTS,
      format: 'number',
      type: 'Average',
      loading: avgWeeklyPostsPending,
    },
  ];

  if (hasMultiChannels) {
    metrics.unshift(...multiChannelMetrics);
  }

  if (!isConnected && isInTTCM)
    return metrics.filter((metric) => notConnectedInTTCMMetrics.includes(metric.title));
  if (isConnected && !isInTTCM)
    return metrics.filter((metric) => connectedNotInTTCMMetrics.includes(metric.title));

  return metrics.filter((metric) => metric.title !== 'Video Count');
}

export function getTTPerformanceMetrics(account) {
  const isConnected = account?.invitationStatus === inviteStatusDetails.ACCEPTED.value;
  const isInTTCM = account?.ttcmStatus === 'IN';

  const notConnectedInTTCMMetrics = [
    'Followers',
    'Avg. Eng. Rate',
    'Avg. EMV',
    'Avg. Engagements',
    'Avg. Video Views',
    'Avg. Weekly Posts',
    'EMV',
    'Likes',
    'Video Count',
    'Median Views',
  ];
  const connectedNotInTTCMMetrics = [
    'Followers',
    'Avg. Eng. Rate',
    'Avg. EMV',
    'Avg. Engagements',
    'Avg. Video Views',
    'Avg. Weekly Posts',
    'EMV',
  ];

  const metrics = [
    {
      title: 'Followers',
      value: account?.followers,
      tooltip: tikTokCreatorMetricTooltips.FOLLOWERS,
      format: 'number',
      type: 'Total',
      channel: creatorSource.TIKTOK,
    },
    {
      title: 'Avg. Eng. Rate',
      value: account?.avgEngagementRate,
      tooltip: tikTokCreatorMetricTooltips.AVG_ENGAGEMENT_RATE,
      format: 'percent',
      type: 'Average',
      channel: creatorSource.TIKTOK,
    },
    {
      title: 'Avg. EMV',
      value: account?.avgEmv,
      tooltip: tikTokCreatorMetricTooltips.AVG_EMV,
      format: 'currency',
      type: 'Average',
      channel: creatorSource.TIKTOK,
    },
    {
      title: 'Avg. Engagements',
      value: account?.avgEngagements,
      tooltip: tikTokAccountPerformanceMetricTooltips.AVG_ENGAGEMENTS,
      format: 'number',
      type: 'Average',
      channel: creatorSource.TIKTOK,
    },
    {
      title: 'Avg. Video Views',
      value: account?.avgVideoViews,
      tooltip: tikTokAccountPerformanceMetricTooltips.AVG_VIDEO_VIEWS,
      format: 'number',
      type: 'Average',
      channel: creatorSource.TIKTOK,
    },
    {
      title: 'Avg. Weekly Posts',
      value: account?.avgWeeklyPosts,
      tooltip: tikTokAccountPerformanceMetricTooltips.AVG_WEEKLY_POSTS,
      format: 'number',
      type: 'Average',
      channel: creatorSource.TIKTOK,
    },
    {
      title: 'EMV',
      value: account?.totalEmv,
      tooltip: tikTokAccountPerformanceMetricTooltips.TOTAL_EMV,
      format: 'currency',
      type: 'Total',
      channel: creatorSource.TIKTOK,
    },
    {
      title: 'Likes',
      value: account?.totalLikes,
      tooltip: tikTokAccountPerformanceMetricTooltips.TOTAL_LIKES,
      format: 'number',
      type: 'Total',
      channel: creatorSource.TIKTOK,
    },
    {
      title: 'Video Count',
      value: account?.totalPostCount,
      tooltip: tikTokAccountPerformanceMetricTooltips.VIDEO_COUNT,
      format: 'number',
      type: 'Total',
      channel: creatorSource.TIKTOK,
    },
    {
      title: 'Median Views',
      value: account?.medianViews,
      tooltip: tiktokDiscoveryFilterTooltips.MEDIAN_VIEWS,
      format: 'number',
      type: 'Total',
      channel: creatorSource.TIKTOK,
    },
  ];

  if (!isConnected && isInTTCM)
    return metrics.filter((metric) => notConnectedInTTCMMetrics.includes(metric.title));
  if (isConnected && !isInTTCM)
    return metrics.filter((metric) => connectedNotInTTCMMetrics.includes(metric.title));

  return metrics.filter((metric) => metric.title !== 'Video Count');
}

export function getInstagramAccountPerformanceMetrics(account, hasMultiChannels) {
  const isConnected = account?.invitationStatus === inviteStatusDetails.ACCEPTED.value;

  const metrics = [
    {
      title: isConnected ? 'Avg. Reach' : 'Avg. Est. Reach',
      value: account?.rollingAccountMetrics.avgReach,
      tooltip: isConnected
        ? creatorPerformanceMetricTooltips.AVG_REACH
        : creatorPerformanceMetricTooltips.AVG_ESTIMATED_REACH,
      format: 'number',
      type: 'Average',
    },
  ];
  const multiChannelMetrics = [
    {
      title: 'Avg. Engagements',
      value: account?.rollingAccountMetrics.avgEngagements,
      tooltip: creatorPerformanceMetricTooltips.AVG_ENGAGEMENTS,
      format: 'number',
      type: 'Average',
    },
    {
      title: 'Avg. Effectiveness',
      value: account?.rollingAccountMetrics.avgEffectiveness,
      tooltip: creatorPerformanceMetricTooltips.AVG_EFFECTIVENESS,
      format: 'percent',
      type: 'Average',
    },
    {
      title: 'Avg. Weekly Posts',
      value: account?.rollingAccountMetrics.avgWeeklyPosts,
      tooltip: creatorPerformanceMetricTooltips.AVG_WEEKLY_POSTS,
      format: 'number',
      type: 'Average',
    },
  ];

  if (hasMultiChannels) {
    metrics.unshift(...multiChannelMetrics);
  }
  return metrics;
}

export function getYTPerformanceMetrics(account) {
  const metrics = [
    {
      title: 'Subscribers',
      value: account?.followers,
      tooltip: youtubePerformanceMetricTooltips.FOLLOWERS,
      format: 'number',
      type: 'Total',
      channel: creatorSource.YOUTUBE,
    },
    {
      title: 'Video Count',
      value: account?.rollingAccountMetrics?.postCount,
      tooltip: youtubePerformanceMetricTooltips.POST_COUNT,
      format: 'number',
      type: 'Total',
      channel: creatorSource.YOUTUBE,
    },
    {
      title: 'Avg. Weekly Posts',
      value: account?.rollingAccountMetrics?.avgWeeklyPosts,
      tooltip: youtubePerformanceMetricTooltips.AVG_WEEKLY_POSTS,
      format: 'number',
      type: 'Average',
      channel: creatorSource.YOUTUBE,
      flag: 'hideInternalYouTubeCreatorMetrics',
    },
    {
      title: 'Avg. Engagements',
      value: account?.rollingAccountMetrics?.avgEngagements,
      tooltip: youtubePerformanceMetricTooltips.AVG_ENGAGEMENTS,
      format: 'number',
      type: 'Average',
      channel: creatorSource.YOUTUBE,
      flag: 'hideInternalYouTubeCreatorMetrics',
    },
    {
      title: 'Avg. Eng. Rate',
      value: account?.rollingAccountMetrics?.avgEngagementRate,
      tooltip: youtubePerformanceMetricTooltips.AVG_ENGAGEMENT_RATE,
      format: 'percent',
      type: 'Average',
      channel: creatorSource.YOUTUBE,
      flag: 'hideInternalYouTubeCreatorMetrics',
    },
    {
      title: 'Avg. EMV',
      value: account?.rollingAccountMetrics?.avgEmv,
      tooltip: youtubePerformanceMetricTooltips.AVG_EMV,
      format: 'currency',
      type: 'Average',
      channel: creatorSource.YOUTUBE,
      flag: 'hideInternalYouTubeCreatorMetrics',
    },
    {
      title: 'Views',
      value: account?.rollingAccountMetrics?.totalViews,
      tooltip: youtubePerformanceMetricTooltips.VIEWS,
      format: 'number',
      type: 'Total',
      channel: creatorSource.YOUTUBE,
    },
    {
      title: 'Avg. Views',
      value: account?.rollingAccountMetrics?.avgViews,
      tooltip: youtubePerformanceMetricTooltips.AVG_VIEWS,
      format: 'number',
      type: 'Average',
      channel: creatorSource.YOUTUBE,
      flag: 'hideInternalYouTubeCreatorMetrics',
    },
    {
      title: 'Likes',
      value: account?.rollingAccountMetrics?.totalLikes,
      tooltip: youtubePerformanceMetricTooltips.LIKES,
      format: 'number',
      type: 'Total',
      channel: creatorSource.YOUTUBE,
      flag: 'hideInternalYouTubeCreatorMetrics',
    },
    {
      title: 'Avg. Likes',
      value: account?.rollingAccountMetrics?.avgLikes,
      tooltip: youtubePerformanceMetricTooltips.AVG_LIKES,
      format: 'number',
      type: 'Average',
      channel: creatorSource.YOUTUBE,
      flag: 'hideInternalYouTubeCreatorMetrics',
    },
    {
      title: 'Comments',
      value: account?.rollingAccountMetrics?.totalComments,
      tooltip: youtubePerformanceMetricTooltips.COMMENTS,
      format: 'number',
      type: 'Total',
      channel: creatorSource.YOUTUBE,
      flag: 'hideInternalYouTubeCreatorMetrics',
    },
    {
      title: 'Avg. Comments',
      value: account?.rollingAccountMetrics?.avgComments,
      tooltip: youtubePerformanceMetricTooltips.AVG_COMMENTS,
      format: 'number',
      type: 'Average',
      channel: creatorSource.YOUTUBE,
      flag: 'hideInternalYouTubeCreatorMetrics',
    },
  ];
  return metrics;
}

export function getIGPerformanceMetrics(account) {
  const isConnected = account?.invitationStatus === inviteStatusDetails.ACCEPTED.value;

  const metrics = [
    {
      title: 'Followers',
      value: account?.rollingAccountMetrics?.followers,
      tooltip: creatorPerformanceMetricTooltips.FOLLOWERS,
      format: 'number',
      type: 'Total',
      channel: creatorSource.INSTAGRAM,
    },
    {
      title: 'Avg. Eng. Rate',
      value: account?.rollingAccountMetrics?.avgEngagementRate,
      tooltip: creatorPerformanceMetricTooltips.AVG_ENGAGEMENT_RATE,
      format: 'percent',
      type: 'Average',
      channel: creatorSource.INSTAGRAM,
    },
    {
      title: 'Avg. EMV',
      value: account?.rollingAccountMetrics?.avgEmv,
      tooltip: creatorPerformanceMetricTooltips.TOTAL_EMV,
      format: 'currency',
      type: 'Average',
      channel: creatorSource.INSTAGRAM,
    },
    {
      title: 'Avg. Engagements',
      value: account?.rollingAccountMetrics.avgEngagements,
      tooltip: creatorPerformanceMetricTooltips.AVG_ENGAGEMENTS,
      format: 'number',
      type: 'Average',
      channel: creatorSource.INSTAGRAM,
    },
    {
      title: 'Avg. Effectiveness',
      value: account?.rollingAccountMetrics.avgEffectiveness,
      tooltip: creatorPerformanceMetricTooltips.AVG_EFFECTIVENESS,
      format: 'percent',
      type: 'Average',
      channel: creatorSource.INSTAGRAM,
    },
    {
      title: 'Avg. Weekly Posts',
      value: account?.rollingAccountMetrics.avgWeeklyPosts,
      tooltip: creatorPerformanceMetricTooltips.AVG_WEEKLY_POSTS,
      format: 'number',
      type: 'Average',
      channel: creatorSource.INSTAGRAM,
    },
    {
      title: isConnected ? 'Avg. Reach' : 'Avg. Est. Reach',
      value: account?.rollingAccountMetrics.avgReach,
      tooltip: isConnected
        ? creatorPerformanceMetricTooltips.AVG_REACH
        : creatorPerformanceMetricTooltips.AVG_ESTIMATED_REACH,
      format: 'number',
      type: 'Average',
      channel: creatorSource.INSTAGRAM,
    },
    {
      title: 'Engaged Accounts',
      value: account?.engagedAccounts,
      tooltip: creatorPerformanceMetricTooltips.ENGAGED_ACCOUNTS,
      format: 'number',
      type: 'Total',
      channel: creatorSource.INSTAGRAM,
      igcmMetric: true,
    },
    {
      title: 'Reach',
      value: account?.reach,
      tooltip: creatorPerformanceMetricTooltips.REACH,
      format: 'number',
      type: 'Total',
      channel: creatorSource.INSTAGRAM,
      igcmMetric: true,
    },
    {
      title: 'Interaction Rate',
      value: account?.reelsInteractionRate != null ? account.reelsInteractionRate / 100 : null,
      tooltip: creatorPerformanceMetricTooltips.INTERACTION_RATE,
      format: 'percent',
      type: 'Average',
      channel: creatorSource.INSTAGRAM,
      igcmMetric: true,
    },
    {
      title: 'Hook Rate',
      value: account?.reelsHookRate != null ? account.reelsHookRate / 100 : null,
      tooltip: creatorPerformanceMetricTooltips.HOOK_RATE,
      format: 'percent',
      type: 'Average',
      channel: creatorSource.INSTAGRAM,
      igcmMetric: true,
    },
  ];
  return metrics;
}

export function getMultiChannelCreatorMetrics(tiktokAccount, instagramAccount) {
  if (!tiktokAccount) {
    const instagramMetrics = getInstagramAccountPerformance(instagramAccount);
    return instagramMetrics.map((metric) => ({ ...metric, channel: creatorSource.INSTAGRAM }));
  }

  return [
    {
      title: 'Followers',
      value: tiktokAccount?.followers,
      tooltip: tikTokCreatorMetricTooltips.FOLLOWERS,
      format: 'number',
      type: 'Total',
      channel: creatorSource.TIKTOK,
    },
    {
      title: 'Avg. Eng. Rate',
      value: tiktokAccount?.avgEngagementRate,
      tooltip: tikTokCreatorMetricTooltips.AVG_ENGAGEMENT_RATE,
      format: 'percent',
      type: 'Average',
      channel: creatorSource.TIKTOK,
    },
    {
      title: 'Avg. EMV',
      value: tiktokAccount?.avgEmv,
      tooltip: tikTokCreatorMetricTooltips.AVG_EMV,
      format: 'currency',
      type: 'Average',
      channel: creatorSource.TIKTOK,
    },
    {
      title: 'Followers',
      value: instagramAccount?.rollingAccountMetrics?.followers,
      tooltip: creatorPerformanceMetricTooltips.FOLLOWERS,
      format: 'number',
      type: 'Total',
      channel: creatorSource.INSTAGRAM,
    },
    {
      title: 'Avg. Eng. Rate',
      value: instagramAccount?.rollingAccountMetrics?.avgEngagementRate,
      tooltip: creatorPerformanceMetricTooltips.AVG_ENGAGEMENT_RATE,
      format: 'percent',
      type: 'Average',
      channel: creatorSource.INSTAGRAM,
    },
    {
      title: 'Avg. EMV',
      value: instagramAccount?.rollingAccountMetrics?.avgEmv,
      tooltip: creatorPerformanceMetricTooltips.TOTAL_EMV,
      format: 'currency',
      type: 'Average',
      channel: creatorSource.INSTAGRAM,
    },
  ];
}

export function getCreatorOverviewPerformance(accounts) {
  if (!accounts) return [];

  const metrics = [];

  const metricMap = {
    instagram: {
      source: creatorSource.INSTAGRAM,
      path: (acc) => acc?.rollingAccountMetrics,
      tooltips: creatorPerformanceMetricTooltips,
    },
    tiktok: {
      source: creatorSource.TIKTOK,
      path: (acc) => acc,
      tooltips: tikTokCreatorMetricTooltips,
    },
    youtube: {
      source: creatorSource.YOUTUBE,
      path: (acc) => acc?.rollingAccountMetrics,
      tooltips: youtubePerformanceMetricTooltips,
    },
  };

  Object.entries(metricMap).forEach(([platform, config]) => {
    const account = accounts?.[platform];
    if (!account) return;

    const data = config.path(account);
    if (!data) return;

    metrics.push(
      {
        title: 'Avg. EMV',
        value: data.avgEmv,
        tooltip: config.tooltips.AVG_EMV,
        format: 'currency',
        type: 'Average',
        channel: config.source,
      },
      {
        title: 'Avg. Eng. Rate',
        value: data.avgEngagementRate,
        tooltip: config.tooltips.AVG_ENGAGEMENT_RATE,
        format: 'percent',
        type: 'Average',
        channel: config.source,
      },
      {
        title: 'Avg. Engagements',
        value: data.avgEngagements,
        tooltip: config.tooltips.AVG_ENGAGEMENTS,
        format: 'number',
        type: 'Average',
        channel: config.source,
      },
    );
  });
  return metrics;
}

export function getTikTokCreatorMetrics(account) {
  return [
    {
      title: 'Followers',
      value: account?.followers,
      tooltip: tikTokCreatorMetricTooltips.FOLLOWERS,
      format: 'number',
      type: 'Total',
    },
    {
      title: 'Avg. Eng. Rate',
      value: account?.avgEngagementRate,
      tooltip: tikTokCreatorMetricTooltips.AVG_ENGAGEMENT_RATE,
      format: 'percent',
      type: 'Average',
    },
    {
      title: 'Avg. EMV',
      value: account?.avgEmv,
      tooltip: tikTokCreatorMetricTooltips.AVG_EMV,
      format: 'currency',
      type: 'Average',
    },
    {
      title: 'Avg. Engagements',
      value: account?.avgEngagements,
      tooltip: tikTokCreatorMetricTooltips.AVG_ENGAGEMENTS,
      format: 'number',
      type: 'Average',
    },
    {
      title: 'Avg. Video Views',
      value: account?.avgVideoViews,
      tooltip: tikTokCreatorMetricTooltips.AVG_VIDEO_VIEWS,
      format: 'number',
      type: 'Average',
    },
    {
      title: 'Avg. Weekly Posts',
      value: account?.avgWeeklyPosts,
      tooltip: tikTokCreatorMetricTooltips.AVG_WEEKLY_POSTS,
      format: 'number',
      type: 'Average',
    },
  ];
}

export function getTikTokPostInsightsNotConnected(account) {
  return [
    {
      title: 'Avg. Likes',
      value: account?.postInsightMetrics?.avgLikes,
      tooltip: tiktokPostInsightsMetricTooltips.AVG_LIKES,
      format: 'number',
      type: 'Average',
    },
    {
      title: 'Avg. Comments',
      value: account?.postInsightMetrics?.avgComments,
      tooltip: tiktokPostInsightsMetricTooltips.AVG_COMMENTS,
      format: 'number',
      type: 'Average',
    },
    {
      title: 'Avg. Shares',
      value: account?.postInsightMetrics?.avgShares,
      tooltip: tiktokPostInsightsMetricTooltips.AVG_SHARES,
      format: 'number',
      type: 'Average',
    },
    {
      title: 'Avg. Video Views',
      value: account?.postInsightMetrics?.avgVideoViews,
      tooltip: tiktokPostInsightsMetricTooltips.AVG_VIDEO_VIEWS,
      format: 'number',
      type: 'Average',
    },
    {
      title: 'EMV',
      value: account?.postInsightMetrics?.totalEmv,
      tooltip: tiktokPostInsightsMetricTooltips.TOTAL_EMV,
      format: 'currency',
      type: 'Total',
    },
  ];
}

export function getTikTokPostInsightsConnected(account) {
  return [
    ...getTikTokPostInsightsNotConnected(account),
    {
      title: 'Avg. Engagements',
      value: account?.postInsightMetrics?.avgEngagements,
      tooltip: tiktokPostInsightsMetricTooltips.AVG_ENGAGEMENT,
      format: 'number',
      type: 'Average',
    },
    {
      title: 'Avg. Eng. Rate',
      value: account?.postInsightMetrics?.avgEngagementRate,
      tooltip: tiktokPostInsightsMetricTooltips.AVG_ENGAGEMENT_RATE,
      format: 'percent',
      type: 'Average',
    },
  ];
}

export function getInstagramPostInsights(account) {
  return [
    {
      title: 'Avg. Eng. Rate',
      value: account?.riqAccountMetrics?.avgEngagementRate,
      tooltip: instagramPostInsightsMetricTooltips.AVG_ENGAGEMENT_RATE,
      format: 'percent',
      type: 'Average',
    },
    {
      title: 'Avg. Engagements',
      value: account?.riqAccountMetrics?.avgEngagements,
      tooltip: instagramPostInsightsMetricTooltips.AVG_ENGAGEMENT_PER_POST,
      format: 'number',
      type: 'Average',
    },
    {
      title: 'Followers Gained',
      value: account?.riqAccountMetrics?.totalFollowersGained,
      tooltip: instagramPostInsightsMetricTooltips.TOTAL_FOLLOWERS_GAINED,
      format: 'number',
      type: 'Total',
    },
    {
      title: 'Avg. Effectiveness',
      value: account?.riqAccountMetrics?.avgEffectiveness,
      tooltip: instagramPostInsightsMetricTooltips.AVG_EFFECTIVENESS,
      format: 'percent',
      type: 'Average',
    },
    {
      title: 'EMV',
      value: account?.riqAccountMetrics?.totalEmv,
      tooltip: instagramPostInsightsMetricTooltips.TOTAL_EMV,
      format: 'currency',
      type: 'Total',
    },
  ];
}

export function getInstagramStoryInsights(account, hasInstagramMetricUpdates = false) {
  return [
    {
      title: 'Avg. Reach',
      value: account?.riqAccountMetrics?.avgReach,
      tooltip: instagramStoryInsightsMetricTooltips.AVG_REACH,
      format: 'number',
      type: 'Average',
    },
    ...(hasInstagramMetricUpdates
      ? [
          {
            title: 'Avg. Views',
            value: account?.riqAccountMetrics?.avgVideoViews,
            tooltip: instagramStoryInsightsMetricTooltips.AVG_VIDEO_VIEWS,
            format: 'number',
            type: 'Average',
          },
        ]
      : [
          {
            title: 'Avg. Impressions',
            value: account?.riqAccountMetrics?.avgImpressions,
            tooltip: instagramStoryInsightsMetricTooltips.AVG_IMPRESSIONS,
            format: 'number',
            type: 'Average',
          },
        ]),
    {
      title: 'Avg. Exit Rate',
      value: account?.riqAccountMetrics?.avgExitRate,
      tooltip: hasInstagramMetricUpdates
        ? instagramStoryInsightsMetricTooltips.AVG_EXIT_RATE_VIEWS
        : instagramStoryInsightsMetricTooltips.AVG_EXIT_RATE,
      format: 'percent',
      type: 'Average',
    },
    {
      title: 'Avg. Completion Rate',
      value: account?.riqAccountMetrics?.avgCompletionRate,
      tooltip: hasInstagramMetricUpdates
        ? instagramStoryInsightsMetricTooltips.AVG_COMPLETION_RATE_VIEWS
        : instagramStoryInsightsMetricTooltips.AVG_COMPLETION_RATE,
      format: 'percent',
      type: 'Average',
    },
    {
      title: 'EMV',
      value: account?.riqAccountMetrics?.totalEmv,
      tooltip: instagramPostInsightsMetricTooltips.TOTAL_EMV,
      format: 'currency',
      type: 'Total',
    },
  ];
}

export function getYouTubePostInsights(account) {
  return [
    {
      title: 'EMV',
      value: account?.riqAccountMetrics?.totalEmv,
      tooltip: youtubePostInsightsMetricTooltips.TOTAL_EMV,
      format: 'currency',
      type: 'Total',
      flag: 'hideInternalYouTubeCreatorMetrics',
    },
    {
      title: 'Views',
      value: account?.riqAccountMetrics?.totalViews,
      tooltip: youtubePostInsightsMetricTooltips.TOTAL_VIEWS,
      format: 'number',
      type: 'Total',
    },
    {
      title: 'Avg. Views',
      value: account?.riqAccountMetrics?.avgViews,
      tooltip: youtubePostInsightsMetricTooltips.AVG_VIEWS,
      format: 'number',
      type: 'Average',
      flag: 'hideInternalYouTubeCreatorMetrics',
    },
    {
      title: 'Avg. Engagements',
      value: account?.riqAccountMetrics?.avgEngagements,
      tooltip: youtubePostInsightsMetricTooltips.AVG_ENGAGEMENTS,
      format: 'number',
      type: 'Average',
      flag: 'hideInternalYouTubeCreatorMetrics',
    },
    {
      title: 'Avg. Eng. Rate',
      value: account?.riqAccountMetrics?.avgEngagementRate,
      tooltip: youtubePostInsightsMetricTooltips.AVG_ENGAGEMENT_RATE,
      format: 'percent',
      type: 'Average',
      flag: 'hideInternalYouTubeCreatorMetrics',
    },
    {
      title: 'Likes',
      value: account?.riqAccountMetrics?.totalLikes,
      tooltip: youtubePostInsightsMetricTooltips.TOTAL_LIKES,
      format: 'number',
      type: 'Total',
    },
    {
      title: 'Avg. Likes',
      value: account?.riqAccountMetrics?.avgLikes,
      tooltip: youtubePostInsightsMetricTooltips.AVG_LIKES,
      format: 'number',
      type: 'Average',
      flag: 'hideInternalYouTubeCreatorMetrics',
    },
    {
      title: 'Comments',
      value: account?.riqAccountMetrics?.totalComments,
      tooltip: youtubePostInsightsMetricTooltips.TOTAL_COMMENTS,
      format: 'number',
      type: 'Total',
    },
    {
      title: 'Avg. Comments',
      value: account?.riqAccountMetrics?.avgComments,
      tooltip: youtubePostInsightsMetricTooltips.AVG_COMMENTS,
      format: 'number',
      type: 'Average',
      flag: 'hideInternalYouTubeCreatorMetrics',
    },
  ];
}

export function getSortString(sort) {
  return sort ? `${sort.sortOrder === 'DESC' ? '-' : ''}${sort.selected.value}` : undefined;
}

export function getHasMentionedBrand(value) {
  if (!value) return undefined;
  const mentionedMe = value.includes('MENTIONED_ME');
  const hasNotMentionedMe = value.includes('HAS_NOT_MENTIONED_ME');
  if (mentionedMe && hasNotMentionedMe) return undefined;
  if (mentionedMe) return true;
  if (hasNotMentionedMe) return false;
  return undefined;
}

export function getSentimentValue(sentiment) {
  if (sentiment?.isPositive) {
    return 'POSITIVE';
  }
  if (sentiment?.isNegative) {
    return 'NEGATIVE';
  }
  if (sentiment?.isNeutral) {
    return 'NEUTRAL';
  }
  return null;
}

export function getVisionPredictionValue(prediction) {
  if (prediction === null || prediction === undefined) {
    return null;
  }
  if (prediction > engagementRating.excellent) {
    return 'top';
  }
  if (prediction > engagementRating.good) {
    return 'better';
  }
  return 'less';
}

export const sentimentOptions = [
  {
    label: SENTIMENTS.POSITIVE.text,
    value: SENTIMENTS.POSITIVE.value,
    severity: SENTIMENTS.POSITIVE.severity,
  },
  {
    label: SENTIMENTS.NEUTRAL.text,
    value: SENTIMENTS.NEUTRAL.value,
    severity: SENTIMENTS.NEUTRAL.severity,
  },
  {
    label: SENTIMENTS.NEGATIVE.text,
    value: SENTIMENTS.NEGATIVE.value,
    severity: SENTIMENTS.NEGATIVE.severity,
  },
];

export function getIconFromSource(source) {
  if (source === creatorSource.INSTAGRAM) return 'instagram-color';
  if (source === creatorSource.TIKTOK) return 'tiktok-color';
  if (source === creatorSource.YOUTUBE) return 'youtube-color';
  throw new Error(`No know icon for source ${source}`);
}

export function getMetricLabel(metric, account, hasInstagramMetricUpdates = false) {
  const metricDetail = metricDetails[metric.metricValue];

  if (
    account?.invitationStatus !== inviteStatusDetails.ACCEPTED.value &&
    metricDetail?.disconnectedLabel
  ) {
    return metricDetail.disconnectedLabel;
  }
  if (hasInstagramMetricUpdates && metricDetail?.newLabel) {
    return metricDetail.newLabel;
  }
  return metric.label;
}

export function getMetricPrefix(metric, accountKey) {
  const metricDetail = metricDetails[metric.metricValue];

  if (accountKey.toUpperCase() === creatorSource.INSTAGRAM) {
    return metricDetail?.isRIQMetric ? 'riqAccountMetrics' : 'rollingAccountMetrics';
  }
  if (accountKey.toUpperCase() === creatorSource.TIKTOK) {
    return metricDetail?.isRIQMetric ? 'postInsightMetrics' : null;
  }
  if (accountKey.toUpperCase() === creatorSource.YOUTUBE) {
    if (metric?.isRIQMetric) return 'riqAccountMetrics';
    if (metric.metricValue === 'followers') return null; // subscribers live on root
    return 'rollingAccountMetrics';
  }
  return null;
}

export function getKeywords(keywords) {
  const result = {
    bioIncludes: [],
    bioDoesNotInclude: [],
    captionsInclude: [],
    captionsDoNotInclude: [],
    bioOrCaptionsInclude: [],
  };

  keywords?.forEach(({ shouldInclude, keyword, location }) => {
    if (!keyword || !keyword.length) return;

    if (shouldInclude) {
      if (location === 'bio') result.bioIncludes.push(keyword);
      if (location === 'caption') result.captionsInclude.push(keyword);
      if (location === 'anywhere') result.bioOrCaptionsInclude.push(keyword);
    } else {
      if (location === 'bio' || location === 'anywhere') result.bioDoesNotInclude.push(keyword);
      if (location === 'caption' || location === 'anywhere')
        result.captionsDoNotInclude.push(keyword);
    }
  });

  Object.keys(result).forEach((key) => {
    if (!result[key].length) result[key] = undefined;
  });

  return result;
}

export function getChannelIconTooltip(handle, status) {
  switch (status) {
    case inviteStatusDetails.ACCEPTED.value:
      return `@${handle} is connected`;
    case inviteStatusDetails.INVITATION_SENT.value:
      return `@${handle} has not yet accepted your invitation to connect`;
    case inviteStatusDetails.LAPSED.value:
      return `@${handle} has not accepted your invitation to connect in over 14 days`;
    case inviteStatusDetails.REVOKED.value:
    case inviteStatusDetails.DISCONNECTED.value:
      return `@${handle} has been disconnected. Select analytics will not import until they reconnect.`;
    case inviteStatusDetails.NOT_INVITED.value:
    case null:
    case '':
      return `@${handle} is not connected`;
    default:
      return `@${handle}`;
  }
}

export function truncateFilename(filename) {
  const lastDotIndex = filename.lastIndexOf('.');

  if (lastDotIndex !== -1) {
    const extension = filename.slice(lastDotIndex);
    if (POPULAR_FILE_EXTENSIONS.includes(extension.toLowerCase())) {
      const name = filename.slice(0, lastDotIndex);
      if (name.length > 20) return `${name.slice(0, 20)}...${extension}`;
      return filename;
    }
  }

  return filename.length > 20 ? `${filename.slice(0, 20)}...` : filename;
}

export function createBlobUrlForAttachment(data, mimeType) {
  const binaryArray = base64ToBinaryArray(data);
  return URL.createObjectURL(new Blob([binaryArray], { type: mimeType }));
}

export function getFileType(mimeType) {
  if (mimeType) {
    if (mimeType.startsWith('image/')) return 'image';
    if (
      mimeType.startsWith('text/') ||
      mimeType === 'application/json' ||
      mimeType === 'application/xml'
    ) {
      return 'text';
    }
    if (mimeType.startsWith('video/')) return 'video';
    if (mimeType.startsWith('application/') || mimeType.startsWith('audio/')) {
      return 'document';
    }
  }

  // Assume that if there are no mimeType, it is a Google Drive document
  return 'document';
}

function formatForwardedDate(date) {
  return `${date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })} at ${date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  })}`;
}

export function formatForwardedMessage(message) {
  const { from, date, subject, to, cc, body } = message;
  const formattedDate = formatForwardedDate(date);

  const formatEmails = (emailList) =>
    emailList.map(({ address }) => `&lt;${address}&gt;`).join(', ');

  const forwardedHeader = `
    <br><br>
    <p>${FORWARD_MESSAGE_HEADER}<br>
    From: ${formatEmails(from)}<br>
    Date: ${formattedDate}<br>
    Subject: ${subject}<br>
    To: ${formatEmails(to)}<br>
    ${cc?.length ? `Cc: ${formatEmails(cc)}</p>` : ''}
    <br><br>`;

  return `${forwardedHeader}${body}`;
}

export function extractCommentFromForwardMessageBody(body) {
  return body.replace(new RegExp(`<[^>]+>${FORWARD_MESSAGE_HEADER}.*$`), '');
}

export function sanitizeStoriesFromDMs(postsList) {
  // TODO: rather than clearing out story media links in the frontend after 24 hours, we should
  //  handle this in the backend (likely on import) when time permits.
  return postsList.map((post) => {
    if (
      post.source === visualSources.INSTAGRAM_STORY &&
      post.instagramStoryFrame.importedViaDm &&
      dayjs(post.createdAt).diff(getCurrentDate(), 'hour') < -24
    ) {
      // hide media for all stories > 24 hours old (request from partnerships team)
      post.image = null;
      post.predictions = null;
      if (post.type === 'VIDEO') {
        post.video.sizes = null;
        post.video.thumbnails = null;
      }
      return post;
    }
    // return all other posts normally
    return post;
  });
}

export function monthDayRange(data) {
  let maxDay = 31;
  const month = data?.birthday?.month ?? 0;
  const year = data?.birthday?.year ?? 0;

  if (month > 0) {
    maxDay = new Date(year, month, 0).getDate();
  }
  if (month === 2 && !year) {
    maxDay += 1;
  }
  return [1, maxDay];
}

export const getClampedValue = (value, min, max, defaultMin) =>
  value != null ? clamp(value, min, max, defaultMin) : undefined;

export const getAudienceParameters = ({ age, genderRatio, audienceCountryCode } = {}) => {
  const ageOption = tikTokDiscoveryAgeOptions.find((opt) => opt.value === age);

  return {
    audience: {
      ...(ageOption?.minAge && { minAge: ageOption.minAge }),
      ...(ageOption?.maxAge && { maxAge: ageOption.maxAge }),
      ...(genderRatio && { genderRatio }),
      ...(audienceCountryCode && { countryCodes: [audienceCountryCode] }),
    },
  };
};

export const getInstagramAudienceParameters = ({
  audienceAge,
  audienceGender,
  audienceCountryCode,
} = {}) => {
  const ageValues = audienceAge || [];
  const genderValues = audienceGender || [];

  return {
    audience: {
      ...(audienceCountryCode && { countryCodes: [audienceCountryCode] }),
      ...(ageValues.length > 0 && { age_bucket: ageValues }),
      ...(genderValues.length > 0 && { gender: genderValues }),
    },
  };
};

export const getInstagramDiscoveryRangeParameters = (hasIGCMFlag, { followersRange } = {}) => {
  // For pre-defined list of min/max options
  if (hasIGCMFlag) {
    return {
      followersGreaterThan: followersRange?.min ?? undefined,
      followersLessThan: followersRange?.max ?? undefined,
    };
  }

  // For manual min/max inputs
  const minFollowers = getClampedValue(
    followersRange?.min,
    instagramFilterInputLimit?.minFollowers,
    instagramFilterInputLimit?.maxFollowers,
  );
  const maxFollowers = getClampedValue(
    followersRange?.max,
    instagramFilterInputLimit?.minFollowers,
    instagramFilterInputLimit?.maxFollowers,
  );

  return {
    followersGreaterThan: minFollowers,
    followersLessThan: minFollowers > maxFollowers ? undefined : maxFollowers,
  };
};

export const getTikTokDiscoveryRangeParameters = ({
  followersRange,
  avgViewsRange,
  engagementRateRange,
  creatorRatesRange,
  medianViewsRange,
} = {}) => {
  const minFollowers = getClampedValue(
    followersRange?.min,
    tiktokFilterInputLimit?.minFollowers,
    tiktokFilterInputLimit?.maxFollowers,
  );
  const maxFollowers = getClampedValue(
    followersRange?.max,
    tiktokFilterInputLimit?.minFollowers,
    tiktokFilterInputLimit?.maxFollowers,
  );

  const minAvgViews = getClampedValue(
    avgViewsRange?.min,
    tiktokFilterInputLimit?.minAvgViews,
    tiktokFilterInputLimit?.maxAvgViews,
  );
  const maxAvgViews = getClampedValue(
    avgViewsRange?.max,
    tiktokFilterInputLimit?.minAvgViews,
    tiktokFilterInputLimit?.maxAvgViews,
  );

  const minMedianViews = getClampedValue(
    medianViewsRange?.min,
    tiktokFilterInputLimit?.minMedianViews,
    tiktokFilterInputLimit?.maxMedianViews,
  );
  const maxMedianViews = getClampedValue(
    medianViewsRange?.max,
    tiktokFilterInputLimit?.minMedianViews,
    tiktokFilterInputLimit?.maxMedianViews,
  );

  let minEngagementRate;
  let maxEngagementRate;
  if (engagementRateRange?.min || engagementRateRange?.max) {
    minEngagementRate =
      getClampedValue(
        engagementRateRange?.min,
        tiktokFilterInputLimit?.minEngagementRate,
        tiktokFilterInputLimit?.maxEngagementRate,
      ) / 100;
    maxEngagementRate =
      getClampedValue(
        engagementRateRange?.max,
        tiktokFilterInputLimit?.minEngagementRate,
        tiktokFilterInputLimit?.maxEngagementRate,
      ) / 100;
  }

  let finalMaxEngagementRate;
  if (minEngagementRate >= 0 && maxEngagementRate >= 0) {
    finalMaxEngagementRate = minEngagementRate < maxEngagementRate ? maxEngagementRate : undefined;
  } else {
    finalMaxEngagementRate = maxEngagementRate || undefined;
  }

  // Only include creator rates if at least one value (min or max) is provided
  const hasCreatorRates = creatorRatesRange?.min || creatorRatesRange?.max;
  const minCreatorRate = hasCreatorRates
    ? getClampedValue(
        creatorRatesRange?.min,
        tiktokFilterInputLimit?.minCreatorRates,
        tiktokFilterInputLimit?.maxCreatorRates,
      )
    : undefined;
  const maxCreatorRate = hasCreatorRates
    ? getClampedValue(
        creatorRatesRange?.max,
        tiktokFilterInputLimit?.minCreatorRates,
        tiktokFilterInputLimit?.maxCreatorRates,
      )
    : undefined;

  return {
    followersGreaterThan: minFollowers,
    followersLessThan: minFollowers > maxFollowers ? undefined : maxFollowers,
    minAvgViews,
    maxAvgViews: minAvgViews > maxAvgViews ? undefined : maxAvgViews,
    minEngagementRate: minEngagementRate >= 0 ? minEngagementRate : undefined,
    maxEngagementRate: finalMaxEngagementRate,
    ...(hasCreatorRates && {
      minCreatorRate,
      maxCreatorRate: minCreatorRate > maxCreatorRate ? undefined : maxCreatorRate,
    }),
    minMedianViews,
    maxMedianViews: minMedianViews > maxMedianViews ? undefined : maxMedianViews,
  };
};

export function getFormattedDate(timestamp) {
  return new Date(timestamp).toLocaleDateString('default', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatCurrency(price, currency = 'USD') {
  return Formatters.formatNumber(price, 'currency', {
    formatOptions: { currency },
    includeCurrency: true,
  });
}

export function getAvatar(creator) {
  if (creator?.accounts?.tiktok?.avatar) return { image: creator.accounts.tiktok.avatar };
  if (creator?.accounts?.instagram?.avatar) return { image: creator.accounts.instagram.avatar };
  if (creator?.accounts?.youtube?.avatar) return { image: creator.accounts.youtube.avatar };
  return { icon: 'user-avatar' };
}

export function getHandle(creator) {
  if (creator?.accounts?.tiktok?.handle) return creator.accounts.tiktok.handle;
  if (creator?.accounts?.instagram?.handle) return creator.accounts.instagram.handle;
  if (creator?.accounts?.youtube?.handle) return creator.accounts.youtube.handle;
  return 'Handle Unavailable';
}

export function isValidNumberRange(range, minimumEntry, maximumEntry) {
  const { min, max } = range || {};
  return (
    (min == null || (min >= minimumEntry && min <= maximumEntry)) &&
    (max == null || (max >= minimumEntry && max <= maximumEntry)) &&
    (min == null || max == null || min < max)
  );
}

export function formatPrice(
  price,
  currency = 'USD',
  includeCurrency = false,
  notation = undefined,
) {
  const formatOptions = {
    currency,
    minimumFractionDigits: Number.isInteger(price) ? 0 : 2,
  };

  if (notation) {
    formatOptions.notation = notation;
  }

  return Formatters.formatCurrency(price, {
    formatOptions,
    includeCurrency,
  });
}

export function getProductValue(data) {
  const price = data.variant?.price ?? data.price;

  if (price && data.quantity) {
    return formatPrice(price * data.quantity, data.currencyCode, true);
  }
  return '-';
}

export function getShopifyThumbnailSize({ mediaUrl, thumbnailUrl }, dimensions) {
  const params = [];
  if (dimensions?.w) params.push(`width=${dimensions.w}`);
  if (dimensions?.h) params.push(`height=${dimensions.h}`);
  const paramString = params.length ? `&${params.join('&')}` : '';
  return `${mediaUrl ?? thumbnailUrl}${paramString}`;
}

export function getFanAccountDrawerProps(fan) {
  return {
    source: fan.source,
    handle: fan.handle,
    sourceCreatorId: fan.sourceCreatorId,
    defaultTikTokPostType: creatorPostsTypeConfigs.TT_CREATOR_RIQ_NOT_CONNECTED_OR_NOT_IN_TTCM,
    defaultInstagramPostType: creatorPostsTypeConfigs.IG_RIQ,
  };
}

export async function asyncSafeCopy(promise) {
  // This uses the older `navigator.clipboard` rather than the Clipboard API.
  // It lets us add text to the clipboard from an async setting (unsupported in Safari).
  if (typeof ClipboardItem !== 'undefined' && navigator.clipboard.write) {
    navigator.clipboard.write([new ClipboardItem({ 'text/plain': promise })]);
    await promise; // Resolve promise outside clipboard to throw exception if needed
  } else {
    // Firefox might not publicly support ClipboardItem yet, but works with async
    navigator.clipboard.write(await promise);
  }
}

export function redirectToShopifyOauth(shop, nonce) {
  const clientId = env.paragonShopifyIntegrationClientId;
  const redirectUrl = env.paragonShopifyIntegrationRedirectUrl;
  const scopes = env.paragonShopifyIntegrationScopes;
  const url = `https://${shop}/admin/oauth/authorize?client_id=${clientId}&redirect_uri=${encodeURIComponent(redirectUrl)}&scope=${scopes}&state=${encodeURIComponent(nonce)}`;
  window.location.assign(url);
}

/**
 * Decodes HTML entities in a string.
 * Works in both browser and Node environments.
 *
 * @param {string} text - Text containing HTML entities
 * @returns {string} Decoded text
 */
export function decodeEntities(text) {
  if (!text) return text;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

/**
 * Gets a value from social media accounts in priority order (Instagram > TikTok > YouTube)
 * @param {Object} accounts - The accounts object containing platform data
 * @param {string} path - The path to the value within each account (e.g., 'avatar', 'handle')
 * @param {any} defaultValue - Default value to return if no value found
 * @returns {any} The first non-null value found in priority order
 */
export function getValueByPriority(accounts, path, defaultValue = null) {
  const platforms = PLATFORM_PRIORITY;
  const getNestedValue = (obj, objPath) =>
    objPath.split('.').reduce((current, key) => current?.[key], obj);

  const value = platforms
    .map((platform) => getNestedValue(accounts?.[platform], path))
    .find((val) => val != null);

  return value != null ? value : defaultValue;
}

export function getCreatorProfileLocation(accountLocation, accountSource) {
  // For Instagram, we predict the location and sometimes the prediction is not available yet.
  if (accountLocation === null && accountSource === creatorSource.INSTAGRAM) {
    return {
      value: 'Location processing...',
      tooltip: profileHeaderLocationTooltipLoading,
    };
  }

  if (
    (typeof accountLocation === 'string' && accountLocation) ||
    (typeof accountLocation === 'object' && accountLocation?.country)
  ) {
    const isString = typeof accountLocation === 'string';
    const country = isString ? accountLocation : accountLocation.country;
    const city = isString ? null : accountLocation.city;

    const flag = getCountryFlag(country);
    return {
      value: city ? `${city}, ${flag} ${country}` : `${flag} ${country}`,
      tooltip: profileHeaderLocationTooltipAvailable,
    };
  }
  return {
    value: 'Location unavailable',
    tooltip: profileHeaderLocationTooltipUnavailable,
  };
}

export function getInviteTooltipCopy(creatorEmail) {
  return creatorEmail
    ? 'Email invitation will be sent from "noreply@dashsocial.com" by default'
    : 'Creator email not available. Add an email in Creator Details to continue.';
}

export function getIgDiscoveryFiltersWithValues(filters) {
  const isKeywordObject = (obj) =>
    typeof obj === 'object' && obj !== null && 'shouldInclude' in obj && 'keyword' in obj;
  const hasKeywordValue = (obj) => Boolean(obj.keyword);
  const excludeKeys = ['competitiveMentionsFilterType'];

  function hasValue(value) {
    if (value == null || value === '') return false;

    if (Array.isArray(value)) {
      if (value.length === 0) return false;
      return isKeywordObject(value[0]) ? hasKeywordValue(value[0]) : true;
    }

    if (typeof value === 'object') {
      if (isKeywordObject(value)) return hasKeywordValue(value);
      if ('min' in value && 'max' in value) return value.min != null || value.max != null;
      if ('preset' in value || 'range' in value) {
        // Has value if preset is not default OR range is set
        return value.preset !== 'ALL_TIME' || value.range != null;
      }
    }

    return true;
  }

  return Object.fromEntries(
    Object.entries(filters).filter(([key, value]) => hasValue(value) && !excludeKeys.includes(key)),
  );
}

export function getIgDiscoveryFilterDataSource(filters) {
  const filtersWithValues = getIgDiscoveryFiltersWithValues(filters || {});
  const filterKeys = Object.keys(filtersWithValues);

  const isCitySelected = filters?.locations?.some((location) => location.city);

  if (IGCM_ONLY_FILTERS.some((filter) => filterKeys.includes(filter))) {
    return 'IGCM';
  }
  if (INDEX_ONLY_FILTERS.some((filter) => filterKeys.includes(filter)) || isCitySelected) {
    return 'INDEX';
  }
  return 'ALL';
}
